import { useMemo, useState } from 'react';
import { Award, ExternalLink, Medal, Search, TrendingUp, Trophy } from 'lucide-react';
import { fmt } from '@/pages/Dashboard';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import type { PageviewTopUrl } from '@/types/pageview-report';
import { SortableTh } from './DomainsTab';

type SortKey = keyof Pick<
    PageviewTopUrl,
    'pageviews' | 'unique_sessions' | 'revenue' | 'impressions' | 'rps' | 'rpm' | 'ipvu'
>;

interface Props {
    data: PageviewTopUrl[];
}

function rankIcon(rank: number) {
    if (rank === 1) return <Trophy className="h-3.5 w-3.5 text-yellow-500" />;
    if (rank === 2) return <Award className="h-3.5 w-3.5 text-gray-400" />;
    if (rank === 3) return <Medal className="h-3.5 w-3.5 text-amber-600" />;
    return null;
}

const COLUMNS: { key: SortKey; label: string; hint?: string }[] = [
    { key: 'pageviews', label: 'PV' },
    { key: 'unique_sessions', label: 'Sess' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'impressions', label: 'Impr.' },
    { key: 'rps', label: 'RPS' },
    { key: 'rpm', label: 'RPM' },
    { key: 'ipvu', label: 'IPVU' },
];

export function UrlsTab({ data }: Props) {
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState<SortKey>('pageviews');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    const filtered = useMemo(() => {
        let arr = data;
        if (search.trim()) {
            const s = search.toLowerCase();
            arr = arr.filter(
                (u) =>
                    u.url.toLowerCase().includes(s) ||
                    u.domain.toLowerCase().includes(s) ||
                    (u.post_id || '').includes(s),
            );
        }
        return [...arr].sort((a, b) => {
            const av = a[sortKey] as number;
            const bv = b[sortKey] as number;
            return sortDir === 'desc' ? bv - av : av - bv;
        });
    }, [data, search, sortKey, sortDir]);

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900">
                        Melhores URLs
                    </h3>
                    <span className="text-xs text-gray-500">({filtered.length})</span>
                </div>
                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filtrar URL, domínio ou post_id..."
                        className="h-8 text-sm w-72 pl-8"
                    />
                </div>
            </div>

            {filtered.length === 0 ? (
                <div className="p-12 text-center text-sm text-gray-500">
                    Nenhuma URL encontrada
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500 w-10">
                                    #
                                </th>
                                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                    URL
                                </th>
                                {COLUMNS.map((c) => (
                                    <SortableTh
                                        key={c.key}
                                        label={c.label}
                                        hint={c.hint}
                                        active={sortKey === c.key}
                                        dir={sortDir}
                                        onClick={() => toggleSort(c.key)}
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map((u, idx) => {
                                const rank = idx + 1;
                                const isTop = rank <= 3 && !search.trim();
                                let displayPath = u.path;
                                try {
                                    const parsed = new URL(u.url);
                                    displayPath = parsed.pathname + parsed.search;
                                } catch (_) {
                                    // ignore
                                }
                                return (
                                    <tr key={idx} className={`hover:bg-gray-50 ${isTop ? 'bg-amber-50/30' : ''}`}>
                                        <td className="px-3 py-2.5">
                                            <div className="flex items-center gap-1.5">
                                                {rankIcon(rank)}
                                                <span
                                                    className={`font-semibold ${
                                                        rank === 1
                                                            ? 'text-yellow-700'
                                                            : rank === 2
                                                            ? 'text-gray-600'
                                                            : rank === 3
                                                            ? 'text-amber-700'
                                                            : 'text-gray-400'
                                                    }`}
                                                >
                                                    {rank}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <a
                                                href={u.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-gray-900 hover:text-blue-600 font-mono truncate max-w-[360px] align-middle"
                                                title={u.url}
                                            >
                                                <span className="truncate">{displayPath}</span>
                                                <ExternalLink className="h-3 w-3 opacity-50 flex-shrink-0" />
                                            </a>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-[10px] text-gray-500 truncate max-w-[260px]">
                                                    {u.domain}
                                                </span>
                                                {u.post_id && (
                                                    <Badge variant="outline" className="text-[9px] py-0 h-4">
                                                        #{u.post_id}
                                                    </Badge>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-mono tabular-nums text-gray-700">
                                            {fmt.number(u.pageviews)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-mono tabular-nums text-gray-700">
                                            {fmt.number(u.unique_sessions)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-mono tabular-nums text-emerald-700 font-medium">
                                            {fmt.currency(u.revenue)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-mono tabular-nums text-gray-600">
                                            {fmt.number(u.impressions)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-mono tabular-nums text-gray-900 font-medium">
                                            {fmt.currency(u.rps)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-mono tabular-nums text-gray-900 font-medium">
                                            {fmt.currency(u.rpm)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-mono tabular-nums text-gray-900 font-medium">
                                            {u.ipvu.toFixed(2)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

